import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ElectoralDistrict, Page, PublicationScope, ResultsPublication, ResultsPublicationStatus, StationTally } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, Tabs } from '../../ui/components';

const RESULTS_ROLES = ['RETURNING_OFFICER', 'ELECTORAL_COMMISSIONER'];
const RETURNING_ROLES = ['RETURNING_OFFICER'];
const COMMISSIONER_ROLES = ['ELECTORAL_COMMISSIONER'];
const STATUSES: ResultsPublicationStatus[] = ['DRAFT', 'CERTIFIED', 'PUBLISHED'];

/** GET /results officer console + POST certify (station/seat/national, scope-specific role). */
export function Results() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const canView = roles.some((r) => RESULTS_ROLES.includes(r));
  const canCertifyStation = roles.some((r) => RETURNING_ROLES.includes(r));
  const canCertifySeatOrNational = roles.some((r) => COMMISSIONER_ROLES.includes(r));
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [electionId] = useState(params.get('electionId') ?? '');
  const [status, setStatus] = useState<ResultsPublicationStatus | ''>('');
  const [scope, setScope] = useState<PublicationScope | ''>('');
  const [showCertify, setShowCertify] = useState(false);

  const qp = new URLSearchParams({ limit: '50' });
  if (status) qp.set('status', status);
  if (scope) qp.set('scope', scope);
  if (electionId) qp.set('electionId', electionId);
  const listQ = useApiQuery<Page<ResultsPublication>>(canView ? `/api/v1/m39/results?${qp.toString()}` : null);

  return (
    <div>
      <PageHeader
        title="Results Certification & Publication"
        subtitle="Station → seat → national certification, published as a signed, verifiable feed to C11 OpenGov."
        actions={
          <div className="flex gap-2">
            {canCertifyStation || canCertifySeatOrNational ? (
              <button className="btn-primary" onClick={() => setShowCertify(true)}>
                + Certify a result
              </button>
            ) : null}
            <Link to="/public/results" className="btn-secondary">
              Public results feed →
            </Link>
          </div>
        }
      />
      {!canView ? <RoleNotice roles={RESULTS_ROLES} /> : null}

      {canView ? (
        <>
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <Field label="Scope" className="!mb-0">
              <select className="field-input !w-auto" value={scope} onChange={(e) => setScope(e.target.value as PublicationScope | '')}>
                <option value="">All</option>
                <option value="STATION">Station</option>
                <option value="SEAT">Seat</option>
                <option value="NATIONAL">National</option>
              </select>
            </Field>
            <Field label="Status" className="!mb-0">
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as ResultsPublicationStatus | '')}>
                <option value="">All</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            {electionId ? <span className="text-xs text-slate-400">Filtered to election {electionId}</span> : null}
          </div>
          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No results publications match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Scope</th>
                    <th className="table-head">Outcome</th>
                    <th className="table-head">Valid votes</th>
                    <th className="table-head">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((r) => (
                    <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/elections/results/${r.id}`)}>
                      <td className="table-cell font-medium">{r.scope}</td>
                      <td className="table-cell text-xs">{r.outcome ? <StatusBadge status={r.outcome} /> : '—'}</td>
                      <td className="table-cell text-xs tabular-nums">{r.totalValidVotes}</td>
                      <td className="table-cell">
                        <StatusBadge status={r.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {showCertify ? (
        <CertifyModal
          canCertifyStation={canCertifyStation}
          canCertifySeatOrNational={canCertifySeatOrNational}
          defaultElectionId={electionId}
          onClose={() => setShowCertify(false)}
          onDone={(created) => {
            setShowCertify(false);
            navigate(`/elections/results/${created.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function CertifyModal({
  canCertifyStation,
  canCertifySeatOrNational,
  defaultElectionId,
  onClose,
  onDone,
}: {
  canCertifyStation: boolean;
  canCertifySeatOrNational: boolean;
  defaultElectionId: string;
  onClose: () => void;
  onDone: (r: ResultsPublication) => void;
}) {
  const [scope, setScope] = useState<PublicationScope>(canCertifyStation ? 'STATION' : 'SEAT');
  const [electionId, setElectionId] = useState(defaultElectionId);
  const [stationTallyId, setStationTallyId] = useState('');
  const [electoralDistrictId, setElectoralDistrictId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const talliesQ = useApiQuery<Page<StationTally>>(scope === 'STATION' && canCertifyStation ? '/api/v1/m39/tallies?status=VERIFIED&limit=100' : null);
  const districtsQ = useApiQuery<Page<ElectoralDistrict>>(scope === 'SEAT' ? '/api/v1/m39/districts?limit=50' : null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      let created: ResultsPublication;
      if (scope === 'STATION') {
        created = await api.post<ResultsPublication>(`/api/v1/m39/results/station/${stationTallyId}/certify`);
      } else if (scope === 'SEAT') {
        created = await api.post<ResultsPublication>(`/api/v1/m39/results/seat/${electoralDistrictId}/certify`, { electionId });
      } else {
        created = await api.post<ResultsPublication>('/api/v1/m39/results/national/certify', { electionId });
      }
      onDone(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Certification failed');
    } finally {
      setBusy(false);
    }
  };

  const valid =
    scope === 'STATION' ? stationTallyId.length > 0 : scope === 'SEAT' ? electionId.length > 0 && electoralDistrictId.length > 0 : electionId.length > 0;

  return (
    <Modal title="Certify a result" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Tabs
          tabs={[
            { key: 'STATION' as PublicationScope, label: 'Station' },
            { key: 'SEAT' as PublicationScope, label: 'Seat' },
            { key: 'NATIONAL' as PublicationScope, label: 'National' },
          ]}
          value={scope}
          onChange={setScope}
        />
        {scope === 'STATION' ? (
          !canCertifyStation ? (
            <RoleNotice roles={RETURNING_ROLES} />
          ) : (
            <Field label="Verified station tally">
              <select className="field-input" value={stationTallyId} onChange={(e) => setStationTallyId(e.target.value)}>
                <option value="">Select…</option>
                {(talliesQ.data?.items ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.pollingStationId} — {t.validVotes} valid votes
                  </option>
                ))}
              </select>
            </Field>
          )
        ) : !canCertifySeatOrNational ? (
          <RoleNotice roles={COMMISSIONER_ROLES} />
        ) : (
          <>
            <Field label="Election id">
              <input className="field-input" value={electionId} onChange={(e) => setElectionId(e.target.value)} placeholder="paste from the election's detail page" />
            </Field>
            {scope === 'SEAT' ? (
              <Field label="Electoral district (seat)">
                <select className="field-input" value={electoralDistrictId} onChange={(e) => setElectoralDistrictId(e.target.value)}>
                  <option value="">Select…</option>
                  {(districtsQ.data?.items ?? []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <p className="text-xs text-slate-500">Requires every active polling station nationwide to already have a VERIFIED tally.</p>
            )}
          </>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
            {busy ? 'Certifying…' : 'Certify'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
